import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAuthToken } from "../lib/api";
import { getDeviceId } from "../lib/device";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Lock } from "lucide-react";
import Brand from "../components/Brand";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("exchanging"); // exchanging | error
  const [error, setError] = useState("");
  const [errorMeta, setErrorMeta] = useState({});
  const { isRTL } = useLanguage();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/");
      return;
    }

    // Strip the session_id from the URL IMMEDIATELY so a refresh / back-button
    // can never replay a stale (one-shot) session_id with the backend.
    window.history.replaceState(null, "", window.location.pathname);

    (async () => {
      try {
        const device_id = getDeviceId();
        const res = await api.post("/auth/session", {
          session_id: m[1],
          device_id,
        });
        // Persist the session_token for the Bearer-token fallback — critical for
        // Safari / cross-site contexts where the cookie may be dropped.
        if (res.data?.session_token) setAuthToken(res.data.session_token);
        setUser(res.data);
        const destination = res.data.role === "admin" ? "/admin" : "/dashboard";
        navigate(destination, { replace: true, state: { user: res.data } });
      } catch (e) {
        const resp = e?.response;
        const httpCode = resp?.status;
        const detail = resp?.data?.detail;

        let detailMessage = "Authentication failed";
        if (typeof detail === "string") {
          // The most common 401 here is "Invalid session_id" — Emergent
          // session_ids are one-shot and expire fast. Translate to a clearer
          // line so the user knows a retry will succeed.
          if (detail === "Invalid session_id") {
            detailMessage = isRTL
              ? "انتهت صلاحية رابط تسجيل الدخول. يرجى إعادة الضغط على \"تسجيل الدخول\"."
              : "Your sign-in link expired. Just click \"Sign in with Google\" again.";
          } else {
            detailMessage = detail;
          }
        } else if (Array.isArray(detail) && detail.length > 0) {
          detailMessage = detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
        } else if (detail && typeof detail === "object") {
          detailMessage = detail.message || detail.error || "Authentication failed";
        } else if (!resp) {
          detailMessage = "Couldn't reach the server. Please check your internet connection and try again.";
        } else if (httpCode) {
          detailMessage = `Server error (${httpCode}). Please try again.`;
        }

        // eslint-disable-next-line no-console
        console.warn("[auth] sign-in error:", { httpCode, detail, message: detailMessage });
        setStatus("error");
        setError(detailMessage);
        setErrorMeta({
          httpCode,
          rawDetail: typeof detail === "string"
            ? detail
            : (detail ? JSON.stringify(detail).slice(0, 300) : null),
        });
      }
    })();
  }, [navigate, setUser, isRTL]);

  if (status === "exchanging") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone">
        <div className="flex items-center gap-3 text-sage">
          <div className="w-2 h-2 rounded-full bg-sage animate-pulse" />
          <div className="w-2 h-2 rounded-full bg-sage animate-pulse" style={{ animationDelay: "150ms" }} />
          <div className="w-2 h-2 rounded-full bg-sage animate-pulse" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    );
  }

  // error
  return (
    <div className="min-h-screen bg-bone flex flex-col items-center justify-center px-4">
      <div className="mb-6">
        <Brand size="md" linkTo="/" />
      </div>
      <div className="w-full max-w-md bg-white border border-border rounded-3xl p-8 text-center">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-terracotta/10 flex items-center justify-center text-terracotta mb-5">
          <Lock className="w-6 h-6" strokeWidth={1.7} />
        </div>
        <h1 className="font-display text-xl font-semibold mb-2">
          {isRTL ? "تعذّر تسجيل الدخول" : "Couldn't sign you in"}
        </h1>
        <p className="text-sm text-muted-foreground mb-3 leading-relaxed" data-testid="auth-error-message">
          {error}
        </p>
        {(errorMeta.httpCode || errorMeta.rawDetail) && (
          <details className="text-[10px] text-muted-foreground/80 bg-secondary/40 rounded px-3 py-2 mb-5 text-start" data-testid="auth-error-details">
            <summary className="cursor-pointer">Technical details</summary>
            <div className="mt-2 break-all">
              {errorMeta.httpCode && <div>HTTP: {errorMeta.httpCode}</div>}
              {errorMeta.rawDetail && <div className="mt-1">Detail: {errorMeta.rawDetail}</div>}
            </div>
          </details>
        )}

        <div className="p-3 rounded-lg bg-secondary/60 text-[11px] text-muted-foreground leading-relaxed text-start mb-5">
          <span className="font-medium text-foreground">
            {isRTL ? "نصيحة:" : "Tip:"}
          </span>{" "}
          {isRTL
            ? "إذا لم تكن رسالة الخطأ تقول إنّ بريدك غير مصرّح به، جرّب تسجيل الخروج من جوجل أولاً ثم أعد المحاولة. إذا كان البريد غير مصرّح له، اطلب من المسؤول إضافته."
            : "If the error doesn't say your email is not authorised, sign out of Google first, then try again. If the email is not authorised, ask your admin to add it to the allowed list."}
        </div>

        <button
          onClick={() => (window.location.href = "/")}
          data-testid="auth-error-retry"
          className="w-full inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-white font-medium px-5 py-2.5 rounded-xl transition-all text-sm mb-2"
        >
          {isRTL ? "حاول تسجيل الدخول مرة أخرى" : "Try signing in again"}
        </button>
        <button
          onClick={() => {
            const continueUrl = window.location.origin + "/";
            window.location.href = "https://accounts.google.com/Logout?continue=" + encodeURIComponent(continueUrl);
          }}
          data-testid="auth-error-google-signout"
          className="w-full inline-flex items-center justify-center gap-2 text-muted-foreground hover:text-sage font-medium px-5 py-2 rounded-xl transition-all text-xs mb-1"
        >
          {isRTL ? "أو: تسجيل الخروج من جوجل أولاً" : "Or: sign out of Google first"}
        </button>
        <button
          onClick={() => (window.location.href = "/")}
          className="w-full inline-flex items-center justify-center gap-2 text-muted-foreground hover:text-sage font-medium px-5 py-2 rounded-xl transition-all text-xs"
        >
          {isRTL ? "العودة إلى الصفحة الرئيسية" : "Back to home"}
        </button>
      </div>
    </div>
  );
}
